import React, { useEffect, useRef, useState } from "react";
import getIcon from "../../utils/Icon";
import tokenApi from "../../api/tokenApi";
import { motion, AnimatePresence } from "framer-motion";
import FlipNumbers from "react-flip-numbers";
import { Copy } from "lucide-react";
import { message, Skeleton } from "antd"; // Импортируем Skeleton

// Иконки блокчейнов
import ethereum from "../../assets/icons/blockchains/ETH.png";
import bsc from "../../assets/icons/blockchains/BNB.png";
import solana from "../../assets/icons/blockchains/solana.png";
import base from "../../assets/icons/blockchains/base.svg";
import sui from "../../assets/icons/blockchains/sui.svg";
import ton from "../../assets/icons/blockchains/TON.png";
import polygon from "../../assets/icons/blockchains/polygon.svg";
import optimism from "../../assets/icons/blockchains/optimism.svg";
import arbitrum from "../../assets/icons/blockchains/arbitrum.svg";
import avalanche from "../../assets/icons/blockchains/avalanche.svg";

const formatCount = (num) => {
    if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
    if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
    return String(num);
};

const chainIcons = {
    ethereum, bsc, solana, base, sui,
    ton, polygon, optimism, arbitrum, avalanche,
};

export default function CoinBadge({ color, count, name, isGiveaway, icon, isCenter }) {
    const [dynamicGradient, setDynamicGradient] = useState("");
    const [currentIcon, setCurrentIcon] = useState(icon);
    const [displayName, setDisplayName] = useState('');
    const [showTooltip, setShowTooltip] = useState(false);
    const [tokenInfo, setTokenInfo] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [isLoadingToken, setIsLoadingToken] = useState(false);
    const [isIconLoaded, setIsIconLoaded] = useState(false);

    const tooltipRef = useRef(null);
    const hideTimer = useRef(null);

    const firstRender = useRef(true);
    useEffect(() => {
        firstRender.current = false;
    }, []);
    const shouldFlip = !firstRender.current;

    // Загрузка данных токена
    useEffect(() => {
        if (!name || name.length < 10) {
            setDisplayName(name);
            // Если icon передан — используем его, иначе дефолтный
            setCurrentIcon(icon || getIcon('img'));
            setTokenInfo(null);
            setIsLoadingToken(false);
            setIsIconLoaded(true);
            return;
        }

        setIsLoadingToken(true);
        setIsIconLoaded(false);

        tokenApi.getTokenByAddress(name)
            .then(raw => {
                const token = raw?.data || raw;
                if (token && token.symbol) {
                    const logo = token.logoUrl
                        || (token.logoPath ? `https://api.duotetra.quest/${token.logoPath}` : null);
                    setDisplayName(token.symbol.toUpperCase());
                    setCurrentIcon(logo || icon);
                    setTokenInfo({ ...token, logoUrl: logo });
                } else {
                    setDisplayName('');
                    setCurrentIcon(icon);
                    setTokenInfo(null);
                }
            })
            .catch(() => {
                setDisplayName('');
                setCurrentIcon(icon);
                setTokenInfo(null);
            })
            .finally(() => {
                setIsLoadingToken(false);
            });
    }, [name, icon]);

    // Подбор градиента по иконке
    useEffect(() => {
        if (!currentIcon) {
            setDynamicGradient("");
            setIsIconLoaded(true);
            return;
        }
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = currentIcon.startsWith("http") ? currentIcon : getIcon(currentIcon);
        img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
            const map = {};
            for (let i = 0; i < data.length; i += 4) {
                const [r, g, b] = [data[i], data[i + 1], data[i + 2]];
                const bright = 0.299 * r + 0.587 * g + 0.114 * b;
                if (bright > 30 && bright < 230) {
                    const c = `rgb(${r},${g},${b})`;
                    map[c] = (map[c] || 0) + 1;
                }
            }
            const sorted = Object.entries(map)
                .sort((a, b) => b[1] - a[1])
                .map(([c]) => c);
            const [c1, c2, c3] = [
                sorted[0] || "rgb(255,255,255)",
                sorted[1] || sorted[0] || "rgb(255,255,255)",
                sorted[2] || sorted[1] || sorted[0] || "rgb(255,255,255)"
            ];
            setDynamicGradient(`linear-gradient(to right, ${c1},${c2},${c3})`);
            setIsIconLoaded(true);
        };
        img.onerror = () => {
            setDynamicGradient("linear-gradient(to right, #e66465, #9198e5)");
            setIsIconLoaded(true);
        };
    }, [currentIcon]);

    // Автоскрытие тултипа count
    useEffect(() => {
        if (showTooltip) {
            const t = setTimeout(() => setShowTooltip(false), 2500);
            return () => clearTimeout(t);
        }
    }, [showTooltip]);

    // Закрытие detail-тултипа по клику внеteleg
    useEffect(() => {
        const onClick = e => {
            if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
                setShowDetails(false);
            }
        };
        if (showDetails) document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [showDetails]);

    return (
        <div className="relative">
            <div
                className={`h-[30px] rounded-2xl p-[1px] ${currentIcon ? "" : color}`}
                style={{
                    background: currentIcon
                        ? dynamicGradient || "linear-gradient(to right, #e66465, #9198e5)"
                        : undefined,
                }}
            >
                <div
                    className={`w-full h-full px-2 bg-main rounded-2xl flex items-center ${
                        isCenter ? "justify-center" : "justify-end gap-2"
                    } ${!isGiveaway && "gap-1"} border border-transparent`}
                >
                    {/* Flip-число и тултип */}
                    <div onClick={() => setShowTooltip(true)} className="relative cursor-pointer">
                        <FlipNumbers
                            height={12}
                            width={8}
                            color="white"
                            background="transparent"
                            play={shouldFlip}
                            perspective={500}
                            numbers={formatCount(count)}
                            duration={1.2}
                        />
                        <AnimatePresence>
                            {showTooltip && (
                                <motion.div
                                    initial={{ opacity: 0, y: -5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -5 }}
                                    transition={{ duration: 0.25 }}
                                    className="absolute left-1/2 -translate-x-1/2 top-[110%]
                             text-xs px-2 py-1 rounded border border-white/10
                             whitespace-nowrap z-10"
                                    style={{
                                        backgroundColor: "#2c2c2e",
                                        color: "#e0e0e0",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.6)",
                                    }}
                                >
                                    {count.toLocaleString()}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Иконка */}
                    <div
                        className="w-[22px] h-[22px] flex items-center justify-center
                         rounded-full overflow-hidden bg-white/10 cursor-pointer"
                        onClick={() => setShowDetails(true)}
                    >
                        {/* Если иконка не загружена или токен грузится, показываем Skeleton */}
                        {!isIconLoaded || isLoadingToken ? (
                            <Skeleton.Avatar active size={22} shape="circle" className="!w-[22px] !h-[22px] !min-w-[22px] !min-h-[22px]" />
                        ) : (
                            <img src={currentIcon } alt={displayName} className="w-full h-full object-contain" />
                        )}
                    </div>

                    {/* Название */}
                    <span
                        className="text-white text-[12px] font-semi cursor-pointer"
                        // Используем flex-grow, чтобы позволить ему занять доступное место,
                        // но Skeleton будет иметь фиксированную ширину
                        style={{ flexGrow: 1, minWidth: '30px', maxWidth: '100px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        onClick={() => setShowDetails(true)}
                    >
            {/* Если токен загружается, показываем Skeleton для текста, иначе displayName */}
                        {isLoadingToken ? (
                            <Skeleton.Input active size="small" style={{ width: '40px', height: '14px', borderRadius: '4px' }} className="!min-w-[40px] !max-w-[40px]" />
                        ) : (
                            displayName || "..."
                        )}
          </span>

                    {isGiveaway && !isLoadingToken && ( // Отображаем "Giveaway" только после загрузки токена
                        <span className="text-white text-[12px] font-semi">Giveaway</span>
                    )}
                </div>
            </div>

            {/* Detail-тултип */}
            <AnimatePresence>
                {showDetails && tokenInfo && (
                    <motion.div
                        ref={tooltipRef}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        transition={{ duration: 0.25 }}
                        onMouseLeave={() => {
                            hideTimer.current = setTimeout(() => setShowDetails(false), 1500);
                        }}
                        onMouseEnter={() => clearTimeout(hideTimer.current)}
                        // Conditional z-index applied here
                        className={`absolute top-full mt-2 w-full flex justify-center ${!isGiveaway ? 'z-[9999]' : 'z-20'}`}
                    >
                        <div className="bg-[#1f1f22] border border-white/10 rounded-xl
               px-4 py-3 shadow-lg max-w-xs w-full">
                            {!isGiveaway ? ( // Condition: When it's NOT a giveaway (compact view, no logoUrl)
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        {/* Token Name and Symbol - NO LOGO HERE */}
                                        <div className="text-[13px] font-bold text-white truncate">
                                            {tokenInfo.name} ({tokenInfo.symbol.toUpperCase()})
                                        </div>
                                    </div>

                                    {/* Blockchain and Copy Address for compact view */}
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {chainIcons[tokenInfo.blockchain] && (
                                            <img
                                                src={chainIcons[tokenInfo.blockchain]}
                                                alt={tokenInfo.blockchain}
                                                className="w-4 h-4"
                                            />
                                        )}
                                        <span className="bg-white/10 px-2 py-[2px] rounded text-white text-xs font-medium">
                                {tokenInfo.blockchain}
                            </span>
                                        <Copy
                                            size={14}
                                            className="text-white/60 cursor-pointer hover:text-white transition"
                                            onClick={() => {
                                                navigator.clipboard.writeText(tokenInfo.address);
                                                message.success("Адрес скопирован!");
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                // Detailed display when isGiveaway is TRUE (original content with logo)
                                <>
                                    <div className="flex items-center gap-3 mb-2">
                                        <img
                                            src={tokenInfo.logoUrl || getIcon('img')}
                                            alt="token"
                                            className="w-10 h-10 rounded-full object-contain"
                                        />
                                        <div>
                                            <div className={`text-[13px] font-bold`}>{tokenInfo.name}</div>
                                            <div className="text-xs text-gray-400">{tokenInfo.symbol.toUpperCase()}</div>
                                        </div>
                                    </div>
                                    <div className="text-xs text-gray-300 flex items-center gap-2 mt-1">
                                        {chainIcons[tokenInfo.blockchain] && (
                                            <img
                                                src={chainIcons[tokenInfo.blockchain]}
                                                alt={tokenInfo.blockchain}
                                                className="w-4 h-4"
                                            />
                                        )}
                                        <span className="bg-white/10 px-2 py-[2px] rounded text-white text-xs font-medium">
                                {tokenInfo.blockchain}
                            </span>
                                    </div>
                                    <div className="text-xs text-gray-400 flex items-center gap-2 mt-2">
                                        Address:
                                        <span className="text-white truncate max-w-[160px]">
                                {tokenInfo.address.slice(0, 6)}…{tokenInfo.address.slice(-4)}
                            </span>
                                        <Copy
                                            size={14}
                                            className="text-white/60 cursor-pointer hover:text-white transition"
                                            onClick={() => {
                                                navigator.clipboard.writeText(tokenInfo.address);
                                                message.success("Адрес скопирован!");
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
